import {
  ActiviteElementKey,
  CompetencesWithChefIndication,
  ElementKey,
  VehiculeMinimized,
} from "@/models";
import DataService from "@/services/data.service";
import { Module, VuexModule, Action, Mutation } from "vuex-class-modules";

/**
 * Read-only reference data ("référentiels") — activity types, locations, equipment, competences, roles.
 * Each field follows the same load-once-cache pattern: call the action, then read the matching getter.
 */
@Module
export class Data extends VuexModule {
  private _typesActivite: ActiviteElementKey[] | null = null;
  private _typesDO: ElementKey[] | null = null;
  private _lieux: ElementKey[] | null = null;
  private _vehicules: VehiculeMinimized[] | null = null;
  private _materiels: ElementKey[] | null = null;
  private _competences: CompetencesWithChefIndication[] | null = null;
  private _roles: ElementKey[] | null = null;

  @Mutation
  private setTypesActivite(types: ActiviteElementKey[]) {
    this._typesActivite = types;
  }

  @Mutation
  private setTypesDO(types: ElementKey[]) {
    this._typesDO = types;
  }

  @Mutation
  private setLieux(lieux: ElementKey[]) {
    this._lieux = lieux;
  }

  @Mutation
  private setVehicules(vehicules: VehiculeMinimized[]) {
    this._vehicules = vehicules;
  }

  @Mutation
  private setMateriels(materiels: ElementKey[]) {
    this._materiels = materiels;
  }

  @Mutation
  private setCompetences(competences: CompetencesWithChefIndication[]) {
    this._competences = competences;
  }

  @Mutation
  private setRoles(roles: ElementKey[]) {
    this._roles = roles;
  }

  /** Loads the activity type list. See {@link getTypesActivite}. */
  @Action
  public async typesActivites(): Promise<void> {
    const types = await await DataService.typesActivite();
    this.setTypesActivite(types);
  }

  /** Loads the "DO" (dispositif opérationnel) type list. See {@link getTypesDO}. */
  @Action
  public async typesDO(): Promise<void> {
    const types = await await DataService.typesDO();
    this.setTypesDO(types);
  }

  /** Loads the minimal vehicle list (for pickers). See {@link getVehicules}. */
  @Action
  public async vehicules(): Promise<void> {
    const vehicules = await await DataService.vehicules();
    this.setVehicules(vehicules);
  }

  /** Loads the location ("lieux") list. See {@link getLieux}. */
  @Action
  public async lieux(): Promise<void> {
    const lieux = await await DataService.lieux();
    this.setLieux(lieux);
  }

  /** Loads the equipment ("matériels") list. See {@link getMateriels}. */
  @Action
  public async materiels(): Promise<void> {
    const materiels = await await DataService.materiels();
    this.setMateriels(materiels);
  }

  /** Loads the competence list, optionally scoped to an activity type. See {@link getCompetences}. */
  @Action
  public async competences(typeActiviteId?: string): Promise<void> {
    const competences = await await DataService.competences(typeActiviteId);
    this.setCompetences(competences);
  }

  /** Loads the assignable role list. See {@link getRoles}. */
  @Action
  public async roles(): Promise<void> {
    const roles = await await DataService.roles();
    this.setRoles(roles);
  }

  get getTypesActivite(): ActiviteElementKey[] | null {
    return this._typesActivite;
  }

  get getTypesDO(): ElementKey[] | null {
    return this._typesDO;
  }

  get getLieux(): ElementKey[] | null {
    return this._lieux;
  }

  get getVehicules(): VehiculeMinimized[] | null {
    return this._vehicules;
  }

  get getMateriels(): ElementKey[] | null {
    return this._materiels;
  }

  get getCompetences(): CompetencesWithChefIndication[] | null {
    return this._competences;
  }

  get getRoles(): ElementKey[] | null {
    return this._roles;
  }
}
